import _ from "lodash";
import moment from "moment";
import "moment-timezone";
import {
    UploadStatus,
    ContributionUploadedFileState,
    ContributionUploadedFile,
    ContributionUploadedFileDetails,
    PendingContribution,
    PendingContributionReport,
    CancelContributionHoldsRequest,
    ContributionFileDetail,
    FundingFileSummaryResponse
} from "@/employer/types";
import * as FileContributionService from "@/shared/api/fileContributionService";
import {
    FetchState,
    PendingContributionsListParams
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { downloadCsvFile } from "@/shared/fileDownload";
import { defineStore } from "pinia";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { useNotifierStore } from "@/shared/store/notifier";

const generateEmptyContributionState = () => ({
    fetchState: FetchState.Uninitialized,
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    pages: [],
    hasExpiredContributions: false
});

export const initialState: ContributionUploadedFileState = {
    fileFetchState: FetchState.Uninitialized,
    fileDetailsFetchState: FetchState.Uninitialized,
    selectedFileId: null,
    selectedFilePayrollDate: null,
    files: [],
    pages: [],
    fileDetails: [],
    pendingContributionsState: generateEmptyContributionState(),
    totalRecords: 0,
    pageNumber: 1,
    showHowItWorks: true,
    isUploadDisabled: false
};

export const useContributionUploadedFileStore = defineStore("contributionUploadedFile", {
    state: (): ContributionUploadedFileState => (_.cloneDeep(initialState)),
    getters: {
        fileListLoaded: state => state.fileFetchState === FetchState.Loaded,
        fileListLoading: state => state.fileFetchState === FetchState.Loading,
        fileDetailsLoaded: state => state.fileDetailsFetchState === FetchState.Loaded,
        fileDetailsLoading: state => state.fileDetailsFetchState === FetchState.Loading,
        shouldDisableUpload: state => state.isUploadDisabled,
        selectedFileDetails: state => _.find(state.fileDetails,
            item => item.file.id === state.selectedFileId) as ContributionUploadedFileDetails,
        pendingContributionsTotalRecords: state => state.pendingContributionsState.totalRecords,
        pendingContributionsPageNumber: state => state.pendingContributionsState.pageNumber,
        pendingContributionsPageCount: state => {
            return state.pendingContributionsState.totalRecords < state.pendingContributionsState.pageSize
                ? 1
                : Math.floor(
                    (state.pendingContributionsState.totalRecords + state.pendingContributionsState.pageSize - 1)
                    / state.pendingContributionsState.pageSize);
        },
        pendingContributionsPageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        getPendingContributions: state => {
            return (state.pendingContributionsState.pages[state.pendingContributionsState.pageNumber]
                || [] as PendingContribution[]);
        },
        pageCount () {
            return this.totalRecords < this.pendingContributionsPageSize ? 1 : Math.floor(
                (this.totalRecords + this.pendingContributionsPageSize - 1) / this.pendingContributionsPageSize);
        },
        contributionsForPage () {
            const pageObject = this.pages[this.pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
        isPendingContributionsLoaded: state => state.pendingContributionsState.fetchState === FetchState.Loaded,
        hasExpiredContributions: state => state.pendingContributionsState.hasExpiredContributions
    },
    actions: {
        setFileFetchState (fetchState: FetchState) {
            this.fileFetchState = fetchState;
        },
        setFileDetailsFetchState (fetchState: FetchState) {
            this.fileDetailsFetchState = fetchState;
        },
        setShowHowItWorks (display: boolean) {
            this.showHowItWorks = display;
        },
        setSelectedFile (file: ContributionUploadedFile) {
            this.selectedFileId = file.id;
            this.selectedFilePayrollDate = file.payrollDate;
        },
        setCancelStatus (id: number) {
            const file = this.files.find(e => e.id === id);
            if (file) {
                file.canCancel = false;
                file.status = UploadStatus.Cancelled;
            }
        },
        addFiles (response: FundingFileSummaryResponse) {
            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== response.totalRecords) {
                this.pages = [];
            }
            const pages = [ ...this.pages ];
            pages[this.pageNumber] = response.files.map(mapFileforDisplay);
            this.totalRecords = response.totalRecords;
            this.isUploadDisabled = response.isUploadDisabled;
            this.pages = pages;

        },
        addFileDetails (fileDetails: ContributionUploadedFileDetails) {
            this.fileDetails = [
                ...this.fileDetails,
                mapFileDetailsForDisplay(fileDetails)
            ];
        },
        resetFileState () {
            this.fileDetails = [];
            this.fileDetailsFetchState = FetchState.Uninitialized;
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setPendingContributionsFetchState (fetchState: FetchState) {
            this.pendingContributionsState.fetchState = fetchState;
        },
        addPendingContributions (
            pendingContributionReport: PendingContributionReport): void {
            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.pendingContributionsState.totalRecords !== pendingContributionReport.totalRecords) {
                this.pendingContributionsState.pages = [];
            }
            const pages = [ ...this.pendingContributionsState.pages ];
            pages[pendingContributionReport.pageNumber] = pendingContributionReport.pendingContributions;
            this.pendingContributionsState.totalRecords = pendingContributionReport.totalRecords;
            this.pendingContributionsState.pageNumber = pendingContributionReport.pageNumber;
            this.pendingContributionsState.pageSize = pendingContributionReport.pageSize;
            this.pendingContributionsState.hasExpiredContributions = pendingContributionReport.hasExpiredContributions;
            this.pendingContributionsState.pages = pages;
        },
        setPendingContributionsPageNumber (pageNumber: number) {
            this.pendingContributionsState.pageNumber = pageNumber;
        },
        clearPendingContributions () {
            // do not clear this check to prevent checkbox from flashing during reset
            const existingHasExpiredItems = this.pendingContributionsState.hasExpiredContributions;

            this.pendingContributionsState = {
                ...generateEmptyContributionState(),
                hasExpiredContributions: existingHasExpiredItems
            };
        },
        clearUploadContributionsState () {
            this.files = [];
            this.pageNumber = 1;
            this.pages = [];
            this.totalRecords = 0;
            this.selectedFileId = null;
            this.fileDetails = [];
            this.fileFetchState = FetchState.Uninitialized;
        },
        loadFileList (
            args: { employerId: number, pageNumber: number, sortOrder: string }) {
            return new Promise<void>((resolve, reject) => {
                this.setPageNumber(args.pageNumber);
                const pageObject = this.pages[this.pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    this.setFileFetchState(FetchState.Loading);
                    FileContributionService
                        .getEmployerContributionFiles(args.employerId, args.pageNumber,
                            this.pendingContributionsPageSize,
                            args.sortOrder)
                        .then(result => {
                            this.addFiles(result.data);
                            this.setFileFetchState(FetchState.Loaded);
                        })
                        .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
                }
            });
        },
        reloadFileList (
            args: { employerId: number, sortOrder: string }) {
            return new Promise((resolve, reject) => {
                this.clearUploadContributionsState();
                this.setFileFetchState(FetchState.Loading);
                FileContributionService
                    .getEmployerContributionFiles(args.employerId, this.pageNumber, this.pendingContributionsPageSize,
                        args.sortOrder)
                    .then(result => {
                        this.addFiles(result.data);
                        this.setFileFetchState(FetchState.Loaded);
                    })
                    .catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        },
        loadFileDetails (
            info: { file: ContributionUploadedFile, employerId: number }) {
            this.setSelectedFile(info.file);
            if (this.fileDetailsFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            const file = _.find(this.fileDetails, x => x.file.id === info.file.id);
            if (file !== undefined || info.file.id === 0) {
                return Promise.resolve();
            }

            return FileContributionService.getEmployerContributionFileDetails(info.employerId, info.file.id)
                .then(result => {
                    this.setFileDetailsFetchState(FetchState.Loading);
                    result.data.file = info.file;
                    this.addFileDetails(result.data);
                    this.setFileDetailsFetchState(FetchState.Loaded);
                });
        },
        fetchPendingContributions (
            { id, pageNumber, displayExpiredOnly }: PendingContributionsListParams) {
            return new Promise<void>((resolve, reject) => {
                this.setPendingContributionsPageNumber(pageNumber);
                const pageObject = this.pendingContributionsState.pages[this.pendingContributionsState.pageNumber ||
                1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    this.setPendingContributionsFetchState(FetchState.Loading);
                    FileContributionService.getPendingContributionsReport(id,
                        this.pendingContributionsState.pageNumber,
                        this.pendingContributionsPageSize, displayExpiredOnly).then(response => {
                            this.addPendingContributions(response.data);
                            this.setPendingContributionsFetchState(FetchState.Loaded);
                        resolve();
                    }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
                }
            });
        },
        processPendingContributionsReport (
            { employerId, displayExpiredContributionsOnly }): Promise<any> {
            return FileContributionService.getPendingContributionsReportDownload(employerId,
                    displayExpiredContributionsOnly)
                .then(response => {
                    const reportData = response.data;
                    const fileName = "pending-contributions-" + moment().format("YYYY-MM-DD") + ".csv";
                    downloadCsvFile(reportData, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        downloadFundingFileSummary (params: { employerId: number, id: number }): Promise<any> {
            return FileContributionService.downloadFundingFileSummary(params.employerId, params.id)
                .then(response => {
                    const fileName = "funding-file-summary" + moment().format("YYYY-MM-DD") + ".csv";
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        cancelContributionHolds (
            params: { employerId: number, ids: number[] }): Promise<any> {
            const request: CancelContributionHoldsRequest = {
                contributionHoldIds: params.ids
            };

            return FileContributionService.cancelContributionHolds(params.employerId, request)
                .then(response => {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Funding processes were canceled successfully");
                    this.clear();
                    this.setPendingContributionsPageNumber(1);
                    this.fetchPendingContributions({ id: params.employerId, pageNumber: 1 });
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        cancelFileProcessing (params: { employerId: number, id: number }): Promise<any> {
            return FileContributionService.cancelFundingFileProcess(params.employerId, params.id)
                .then(response => {
                    const result = response.data;
                    if (!result.isSuccess) {
                        storeHelper.handleError({
                            error: "Failed to process the cancel funding request. please try again", rethrow: false
                        });
                    } else {
                        this.setCancelStatus(result.id);
                        const notifierStore = useNotifierStore();
                        notifierStore.setSuccess("Funding process was canceled successfully");
                    }
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});

function mapFileforDisplay (file): ContributionUploadedFile {
    const dateTimeObject = moment.tz(moment.utc(file.uploadDate), moment.tz.guess());
    const payrollDateObj = moment(file.payrollDate);
    return {
        id: file.id,
        fileName: file.fileName,
        date: file.payrollDate ? payrollDateObj.format("YYYY-MM-DD") : "",
        payrollDate: file.payrollDate ? payrollDateObj.format("MM/DD/YYYY") : "TBD",
        uploadDate: dateTimeObject.format("MM/DD/YYYY"),
        uploadTime: dateTimeObject.format("hh:mm a"),
        status: file.status,
        canCancel: file.canCancel,
        totalAchAmount: file.totalAchAmount,
        fileDetails: file.fileDetails ? file.fileDetails.map(mapDetail) : [],
        hasMultipleUploads: file.hasMultipleUploads,
        contributionEffectiveYear: file.contributionEffectiveYear
    };
}

function mapDetail (file): ContributionFileDetail {
    const dateTimeObject = moment.tz(moment.utc(file.uploadDate), moment.tz.guess());
    const payrollDateObj = moment(file.payrollDate);
    return {
        id: file.id,
        fileName: file.fileName,
        date: file.payrollDate ? payrollDateObj.format("YYYY-MM-DD") : "",
        payrollDate: file.payrollDate ? payrollDateObj.format("MM/DD/YYYY") : "TBD",
        uploadDate: dateTimeObject.format("MM/DD/YYYY"),
        uploadTime: dateTimeObject.format("hh:mm a"),
        status: file.status,
        canCancel: file.canCancel,
        achAmount: file.achAmount,
        contributionEffectiveYear: file.contributionEffectiveYear
    };
}

function mapFileDetailsForDisplay (fileDetails): ContributionUploadedFileDetails {
    return {
        file: fileDetails.file,
        errors: fileDetails.errors,
        numberOfRecords: fileDetails.numberOfRecords,
        employerTotal: fileDetails.employerTotal,
        employeePreTaxTotal: fileDetails.employeePreTaxTotal,
        employeePostTaxTotal: fileDetails.employeePostTaxTotal,
        contributionsTotal: fileDetails.employerTotal + fileDetails.employeePreTaxTotal +
            fileDetails.employeePostTaxTotal,
        onHoldTotal: fileDetails.onHoldTotal,
        includedInACHTotal: (fileDetails.employerTotal + fileDetails.employeePreTaxTotal +
                fileDetails.employeePostTaxTotal)
            - fileDetails.onHoldTotal,
        errorRowCount: fileDetails.errorRowCount
    };
}
